import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, Banknote, Calculator, CalendarDays, Coins, Lock, Printer } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { money, fmtDate } from "@/lib/constants";
import { useLang } from "@/i18n";
import { Panel, Empty } from "@/pages/shared";
import InterimDeposit from "@/components/InterimDeposit";
import CashCount, { denomTotal } from "@/components/CashCount";

const today = () => new Date().toISOString().slice(0, 10);

export default function AdminCashClose() {
  const { t } = useLang();
  const [date, setDate] = useState(today());
  const [rep, setRep] = useState(null);
  const [closures, setClosures] = useState([]);
  const [counted, setCounted] = useState("");
  const [keep, setKeep] = useState("");
  const [alertCfg, setAlertCfg] = useState(null);
  const [countOpen, setCountOpen] = useState(false);
  const [countedDenoms, setCountedDenoms] = useState({});
  const [floatDenoms, setFloatDenoms] = useState({});
  const [weeks, setWeeks] = useState([]);
  const [period, setPeriod] = useState("8");
  const [dep, setDep] = useState(null); // {date, amount, by, at, note}
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [diffHistory, setDiffHistory] = useState(false);

  const load = useCallback(async (d) => {
    try {
      const [r, c] = await Promise.all([api.get(`/cash/day?date=${d}`), api.get("/cash/closures")]);
      setRep(r.data); setClosures(c.data);
      setKeep(String(r.data.float_suggestion ?? 0));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(date); setCounted(""); setNote(""); }, [load, date]);
  useEffect(() => {
    api.get(`/cash/differences?period=${period}`)
      .then(({ data }) => setWeeks(data.weeks))
      .catch((e) => console.warn("Kasverschillen ophalen mislukt", e?.message));
  }, [period, rep?.closed]);

  useEffect(() => {
    api.get("/cash/alert-settings")
      .then(({ data }) => setAlertCfg(data))
      .catch((e) => console.debug("Waarschuwingsinstellingen niet opgehaald", e?.message));
  }, []);

  const saveAlert = async (next) => {
    setAlertCfg(next);
    try { await api.put("/cash/alert-settings", next); toast.success(t("saved")); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const diff = counted === "" ? null : Number(counted) - (rep?.cash_expected ?? rep?.cash_total ?? 0);
  const toDeposit = counted === "" ? null : Number(counted) - (Number(keep) || 0);
  const applyCounted = (next) => { setCountedDenoms(next); setCounted(String(denomTotal(next))); };
  const applyFloat = (next) => { setFloatDenoms(next); setKeep(String(denomTotal(next))); };

  const saveDeposit = async () => {
    try {
      await api.post(`/cash/closures/${dep.date}/deposit`, {
        amount: Number(dep.amount) || 0, by: dep.by, at: dep.at, note: dep.note, bank_account: dep.bank_account || "",
      });
      setDep(null); await load(date); toast.success(t("saved"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const close = async () => {
    setBusy(true);
    try {
      await api.post("/cash/close", { date, counted_cash: counted === "" ? null : Number(counted), note, float_next: keep === "" ? null : Number(keep),
        counted_denoms: Object.keys(countedDenoms).length ? countedDenoms : null,
        float_denoms: Object.keys(floatDenoms).length ? floatDenoms : null });
      await load(date); toast.success(t("cash_closed"));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Panel
      title={t("admin_cashclose")}
      testid="cashclose-panel"
      action={
        <div className="flex items-center gap-2">
          <InterimDeposit onDone={load} />
          <CalendarDays className="h-4 w-4 text-muted-foreground" />
          <Input type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} className="w-40" data-testid="cashclose-date" />
        </div>
      }
    >
      <div className="p-5 space-y-6">
        <p className="text-sm text-muted-foreground">{t("admin_cashclose_desc")}</p>

        {alertCfg && (
          <div className="flex flex-wrap items-end gap-3 rounded-xl border border-border bg-muted/40 p-3" data-testid="cash-alert-settings">
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={alertCfg.enabled} data-testid="cash-alert-enabled"
                onChange={(e) => saveAlert({ ...alertCfg, enabled: e.target.checked })} />
              {t("cash_alert_enabled")}
            </label>
            <div className="space-y-1">
              <Label className="text-xs">{t("cash_alert_limit")}</Label>
              <Input type="number" min="0" step="0.5" value={alertCfg.limit} className="w-24" data-testid="cash-alert-limit"
                onChange={(e) => setAlertCfg({ ...alertCfg, limit: Number(e.target.value) })}
                onBlur={() => saveAlert(alertCfg)} />
            </div>
            <label className="flex items-center gap-2 text-sm font-medium">
              <input type="checkbox" checked={alertCfg.max_cash_enabled} data-testid="cash-max-enabled"
                onChange={(e) => saveAlert({ ...alertCfg, max_cash_enabled: e.target.checked })} />
              {t("cash_max_enabled")}
            </label>
            <div className="space-y-1">
              <Label className="text-xs">{t("cash_max_limit")}</Label>
              <Input type="number" min="0" step="10" value={alertCfg.max_cash} className="w-24" data-testid="cash-max-limit"
                onChange={(e) => setAlertCfg({ ...alertCfg, max_cash: Number(e.target.value) })}
                onBlur={() => saveAlert(alertCfg)} />
            </div>
            <p className="text-[11px] text-muted-foreground flex-1 min-w-[14rem]">{t("cash_alert_hint")} {t("cash_max_hint")}</p>
          </div>
        )}

        {rep && (
          <>
            {((rep.deposits_in_rows || []).length > 0 || (rep.deposits_out_rows || []).length > 0) && (
              <div className="rounded-xl border border-amber-200 bg-amber-50/60 p-4 space-y-1" data-testid="cash-deposits-box">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-amber-800">{t("deposits_cash")}</p>
                {(rep.deposits_in_rows || []).map((r, i) => (
                  <p key={`i${i}`} className="text-[12px] text-foreground/90" data-testid={`cash-deposit-in-${i}`}>
                    + {money(r.amount)} · {r.guest_name} · {r.code} ({t("deposit_received")})
                  </p>
                ))}
                {(rep.deposits_out_rows || []).map((r, i) => (
                  <p key={`o${i}`} className="text-[12px] text-foreground/90" data-testid={`cash-deposit-out-${i}`}>
                    − {money(r.amount)} · {r.guest_name} · {r.code} ({t("deposit_refund")})
                  </p>
                ))}
              </div>
            )}
            {(rep.account_paid_rows || []).length > 0 && (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 dark:bg-emerald-950/20 p-4 space-y-1" data-testid="cash-account-paid-box">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-emerald-800 dark:text-emerald-300">{t("cash_account_paid")}</p>
                {rep.account_paid_rows.map((r, i) => (
                  <p key={`ap${i}`} className="text-[12px] text-foreground/90" data-testid={`cash-account-paid-${i}`}>
                    {r.cash > 0 && <span className="text-emerald-700 dark:text-emerald-400 font-semibold">+ {money(r.cash)} cash</span>}
                    {r.cash > 0 && r.card > 0 && " · "}
                    {r.card > 0 && <span className="text-violet-600">{money(r.card)} {t("cash_card_total")}</span>}
                    {" · "}{r.number || "—"}{r.guest_name ? ` · ${r.guest_name}` : ""}{r.seller ? ` · ${r.seller}` : (r.by ? ` · ${r.by}` : "")}
                  </p>
                ))}
                <p className="text-[11px] text-muted-foreground pt-1">{t("cash_account_hint")}</p>
              </div>
            )}
            {(rep.online_deposit_rows || []).length > 0 && (
              <div className="rounded-xl border border-sky-200 bg-sky-50/60 dark:bg-sky-950/20 p-4 space-y-1" data-testid="cash-online-deposits-box">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-sky-800 dark:text-sky-300">{t("cash_online_deposits")}</p>
                {rep.online_deposit_rows.map((r, i) => (
                  <p key={`od${i}`} className="text-[12px] text-foreground/90" data-testid={`cash-online-deposit-${i}`}>
                    <span className="text-sky-700 dark:text-sky-400 font-semibold">{money(r.amount)}</span>
                    {" · "}{r.pitch_label || "—"}{r.guest_name ? ` · ${r.guest_name}` : ""}
                  </p>
                ))}
                <p className="text-[11px] text-muted-foreground pt-1">{t("cash_online_deposits_hint")}</p>
              </div>
            )}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {[
                ["cash_total", t("pay_cash"), rep.cash_total, "text-emerald-600"],
                ["card_total", t("cash_card_total"), rep.card_total || 0, "text-violet-600"],
                ["account_total", t("pay_account"), rep.account_total, "text-sky-600"],
                ["day_total", t("total"), rep.total, "text-primary"],
                ...(rep.account_paid_cash ? [["account_paid", t("cash_account_paid"), rep.account_paid_cash, "text-emerald-600"]] : []),
                ...(rep.online_deposits ? [["online_dep", t("cash_online_deposits"), rep.online_deposits, "text-sky-600"]] : []),
                ["deposits", t("deposits_cash"), (rep.deposits_in || 0) - (rep.deposits_out || 0), "text-amber-600"],
                ["opening", t("cash_opening"), rep.opening_float || 0, "text-slate-500"],
                ["expected", t("cash_expected"), rep.cash_expected ?? rep.cash_total, "text-emerald-700"],
              ].map(([k, label, val, cls]) => (
                <div key={k} className="rounded-xl border border-border p-4" data-testid={`cash-${k}`}>
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{label}</p>
                  <p className={`font-display font-extrabold text-2xl tabular-nums ${cls}`}>{money(val)}</p>
                </div>
              ))}
              <div className="rounded-xl border border-border p-4" data-testid="cash-count">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{t("sales")}</p>
                <p className="font-display font-extrabold text-2xl tabular-nums text-foreground">{rep.count}</p>
              </div>
            </div>
            {!!rep.card_total && (
              <p className="text-[11px] text-muted-foreground" data-testid="cash-card-hint">{t("cash_card_hint")}</p>
            )}

            {(rep.banked_rows || []).length > 0 && (
              <div className="rounded-xl border border-border p-4" data-testid="cash-banked">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">{t("cash_banked_today")} · {money(rep.banked)}</p>
                {rep.banked_rows.map((b, i) => (
                  <p key={`${b.by}-${i}`} className="text-xs text-muted-foreground" data-testid={`cash-banked-${i}`}>
                    {money(b.amount)} · {b.by}{b.note ? ` · ${b.note}` : ""}
                  </p>
                ))}
              </div>
            )}

            {(rep.sellers || []).length > 0 && (
              <div className="rounded-xl border border-border p-4" data-testid="cash-sellers">
                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">{t("cash_sellers_today")}</p>
                <div className="flex flex-wrap gap-2">
                  {rep.sellers.map((sl) => (
                    <span key={`${sl.seller_name}-${sl.seller_number}`} data-testid={`cash-seller-${sl.seller_number || sl.seller_name}`}
                      className="rounded-lg border border-border px-3 py-1.5 text-xs">
                      <span className="font-semibold">{sl.seller_name}</span>
                      {sl.seller_number ? <span className="text-muted-foreground"> · {sl.seller_number}</span> : null}
                      <span className="text-emerald-600"> · {money(sl.cash)}</span>
                      <span className="text-sky-600"> · {money(sl.account)}</span>
                      <span className="text-muted-foreground"> · {sl.count}×</span>
                    </span>
                  ))}
                </div>
              </div>
            )}

            {rep.vat_summary.length > 0 && (
              <div className="rounded-xl border border-border overflow-hidden" data-testid="cash-vat">
                <Table>
                  <TableHeader><TableRow><TableHead>{t("vat")}</TableHead><TableHead className="text-right">{t("subtotal")}</TableHead><TableHead className="text-right">{t("vat")}</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {rep.vat_summary.map((v) => (
                      <TableRow key={v.rate}>
                        <TableCell className="font-semibold">{v.rate}%</TableCell>
                        <TableCell className="text-right tabular-nums">{money(v.base)}</TableCell>
                        <TableCell className="text-right tabular-nums">{money(v.vat)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {rep.closed ? (
              <div className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 flex items-start gap-2" data-testid="cash-closed-banner">
                <Lock className="h-4 w-4 mt-0.5 shrink-0" />
                <span>
                  <strong>{t("cash_closed")}</strong> — {fmtDate(rep.closure?.closed_at)} · {rep.closure?.closed_by}
                  {rep.closure?.counted_cash !== null && rep.closure?.counted_cash !== undefined &&
                    <> · {t("cash_counted")}: {money(rep.closure.counted_cash)} · {t("cash_difference")}: {money(rep.closure.difference)}</>}
                  {rep.closure?.float_next != null && <> · {t("cash_keep")}: {money(rep.closure.float_next)}</>}
                  {rep.closure?.to_deposit != null && <> · {t("cash_to_deposit")}: {money(rep.closure.to_deposit)}</>}
                  {rep.closure?.auto && <> · {t("cash_auto")}</>}
                </span>
                <Button size="sm" variant="outline" className="ml-auto shrink-0" data-testid="cash-print-closure"
                  onClick={() => window.open(`/print-dagafsluiting?date=${date}`, "_blank")}>
                  <Printer className="h-4 w-4 mr-1" />{t("print")}
                </Button>
                {!!rep.closure?.difference && (
                  <Button size="sm" variant="outline" className="shrink-0 border-rose-300 text-rose-700" data-testid="cash-print-diff"
                    onClick={() => window.open(`/print-kasverschil?date=${date}${diffHistory ? "&history=1" : ""}`, "_blank")}>
                    <Printer className="h-4 w-4 mr-1" />{t("cash_diff_list")}
                  </Button>
                )}
                {!!rep.closure?.difference && (
                  <label className="flex items-center gap-1.5 text-xs shrink-0 cursor-pointer" data-testid="cash-diff-history-toggle">
                    <input type="checkbox" checked={diffHistory} onChange={(e) => setDiffHistory(e.target.checked)} />
                    {t("cash_diff_history_opt")}
                  </label>
                )}
              </div>
            ) : (
              <div className="rounded-xl border border-border p-4 space-y-3" data-testid="cash-close-form">
                {rep.unprinted.length > 0 && (
                  <p className="flex items-start gap-2 text-sm text-amber-700" data-testid="cash-unprinted">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />{t("cash_unprinted")}: {rep.unprinted.join(", ")}
                  </p>
                )}
                <div className="grid gap-3 sm:grid-cols-[10rem_10rem_1fr_auto] items-end">
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("cash_counted")}</Label>
                    <Input type="number" step="0.01" value={counted} onChange={(e) => setCounted(e.target.value)} data-testid="cash-counted-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("cash_keep")}</Label>
                    <Input type="number" step="0.01" min="0" value={keep} onChange={(e) => setKeep(e.target.value)} data-testid="cash-keep-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">{t("note")}</Label>
                    <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="cash-note-input" />
                  </div>
                  <Button onClick={close} disabled={busy || rep.unprinted.length > 0} data-testid="cash-close-button"><Coins className="h-4 w-4 mr-1" />{t("cash_close")}</Button>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setCountOpen((v) => !v)} data-testid="cash-count-toggle">
                    <Calculator className="h-4 w-4 mr-1" />{t(countOpen ? "cash_count_hide" : "cash_count_show")}
                  </Button>
                </div>
                {countOpen && (
                  <div className="grid gap-3 lg:grid-cols-2">
                    <CashCount title={t("cash_counted")} counts={countedDenoms} setCounts={applyCounted} testid="cash-count-counted" />
                    <CashCount title={t("cash_keep")} counts={floatDenoms} setCounts={applyFloat} testid="cash-count-float" />
                  </div>
                )}
                {toDeposit !== null && (
                  <p className="text-sm" data-testid="cash-to-deposit">
                    {t("cash_to_deposit")}: <span className="font-display font-bold tabular-nums text-primary">{money(toDeposit)}</span>
                    <span className="text-muted-foreground"> ({t("cash_keep")}: {money(Number(keep) || 0)})</span>
                  </p>
                )}
                {diff !== null && (
                  <p className="text-sm" data-testid="cash-difference">
                    {t("cash_difference")}: <span className={`font-display font-bold tabular-nums ${diff === 0 ? "text-emerald-600" : "text-rose-600"}`}>{money(diff)}</span>
                  </p>
                )}
              </div>
            )}

            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">{t("sales")} — {date}</p>
              {rep.sales.length === 0 ? <Empty /> : (
                <Table>
                  <TableHeader><TableRow>
                    <TableHead>{t("invoice_number")}</TableHead><TableHead>{t("bill_to")}</TableHead>
                    <TableHead>{t("payment")}</TableHead><TableHead>{t("status")}</TableHead>
                    <TableHead className="text-right">{t("total")}</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {rep.sales.map((s) => (
                      <TableRow key={s.id} data-testid={`cash-sale-${s.id}`}>
                        <TableCell className="font-semibold text-primary tabular-nums">{s.number || "—"}</TableCell>
                        <TableCell>{s.bill_to?.name || s.guest_name}</TableCell>
                        <TableCell className="text-xs">{t(s.payment === "cash" ? "pay_cash" : "pay_account")}</TableCell>
                        <TableCell className="text-xs">{s.printed_at ? t("printed") : <span className="text-amber-700 font-semibold">{t("not_printed")}</span>}</TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">{money(s.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </>
        )}

        <div data-testid="cash-weeks">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{t("cash_diff_weeks")}</p>
            <div className="flex rounded-full border border-border overflow-hidden">
              {[["8", t("cash_last_8")], ["13", t("cash_last_13")], ["year", t("cash_this_year")]].map(([k, lbl]) => (
                <button key={k} onClick={() => setPeriod(k)} data-testid={`cash-period-${k}`}
                  className={`px-3 py-1.5 text-xs font-semibold transition-colors ${period === k ? "bg-secondary text-primary" : "text-muted-foreground hover:bg-muted"}`}>{lbl}</button>
              ))}
            </div>
          </div>
          {weeks.length === 0 ? <Empty /> : (
            <div className="space-y-2">
              {weeks.map((w) => {
                const bad = w.days_with_diff >= 2 || Math.abs(w.total) > (alertCfg?.limit ?? 10);
                return (
                  <div key={w.start} data-testid={`cash-week-${w.start}`}
                    className={`rounded-xl border p-3 ${bad ? "border-rose-300 bg-rose-50/60 dark:bg-rose-950/20" : "border-border"}`}>
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                      <p className="font-display font-bold text-sm">{w.week}</p>
                      <p className="text-xs text-muted-foreground">{w.start} → {w.end}</p>
                      <p className={`text-sm font-semibold tabular-nums ${w.total ? "text-rose-600" : "text-emerald-600"}`}>
                        {t("cash_difference")}: {money(w.total)}
                      </p>
                      <p className="text-xs text-muted-foreground">{t("cash_days_with_diff")}: {w.days_with_diff}/{w.closures}</p>
                      {w.max_diff ? <p className="text-xs text-rose-600">{t("cash_max_diff")}: {money(w.max_diff)}</p> : null}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {w.days.map((d) => (
                        <button key={d.date} onClick={() => setDate(d.date)} data-testid={`cash-week-day-${d.date}`}
                          title={`${d.date} · ${d.difference == null ? "—" : money(d.difference)}`}
                          className={`px-2 py-1 rounded-md text-[11px] tabular-nums border transition-colors ${
                            d.difference ? "border-rose-300 bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300" : "border-border hover:bg-muted text-muted-foreground"}`}>
                          {d.date.slice(8)}/{d.date.slice(5, 7)} · {d.difference == null ? "—" : money(d.difference)}
                        </button>
                      ))}
                    </div>
                    {w.days.filter((d) => d.difference && (d.sellers || []).length).map((d) => (
                      <p key={`s${d.date}`} className="mt-1 text-[11px] text-muted-foreground" data-testid={`cash-week-sellers-${d.date}`}>
                        {d.date}: {d.sellers.map((sl) => `${sl.seller_name} (${money(sl.cash)})`).join(" · ")}
                      </p>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground mb-2">{t("cash_closures")}</p>
          {closures.length === 0 ? <Empty /> : (
            <Table>
              <TableHeader><TableRow>
                <TableHead>{t("date")}</TableHead><TableHead className="text-right">{t("pay_cash")}</TableHead>
                <TableHead className="text-right">{t("pay_account")}</TableHead><TableHead className="text-right">{t("cash_counted")}</TableHead>
                <TableHead className="text-right">{t("cash_keep")}</TableHead>
                <TableHead className="text-right">{t("cash_to_deposit")}</TableHead>
                <TableHead className="text-right">{t("cash_difference")}</TableHead>
                <TableHead>{t("cash_deposit")}</TableHead><TableHead>{t("logged_in_as")}</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {closures.map((c) => (
                  <TableRow key={c.date} data-testid={`closure-row-${c.date}`} className="cursor-pointer" onClick={() => setDate(c.date)}>
                    <TableCell className="font-semibold">{c.date}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(c.cash_total)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(c.account_total)}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.counted_cash == null ? "—" : money(c.counted_cash)}</TableCell>
                    <TableCell className="text-right tabular-nums">{c.float_next == null ? "—" : money(c.float_next)}</TableCell>
                    <TableCell className="text-right tabular-nums font-semibold">{c.to_deposit == null ? "—" : money(c.to_deposit)}</TableCell>
                    <TableCell className={`text-right tabular-nums ${c.difference ? "text-rose-600 font-semibold" : ""}`}>
                      {c.difference == null ? "—" : (
                        <span className="inline-flex items-center gap-1 justify-end">
                          {money(c.difference)}
                          {!!c.difference && (
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-rose-600" data-testid={`closure-diff-print-${c.date}`}
                              title={t("cash_diff_list")} onClick={(e) => { e.stopPropagation(); window.open(`/print-kasverschil?date=${c.date}${diffHistory ? "&history=1" : ""}`, "_blank"); }}>
                              <Printer className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs" onClick={(e) => e.stopPropagation()}>
                      {c.deposited_by ? (
                        <span className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400" data-testid={`closure-deposit-${c.date}`}>
                          {money(c.deposited_amount)} · {c.deposited_by} · {c.deposited_on}
                          {c.deposit_id && (
                            <Button variant="ghost" size="sm" data-testid={`closure-deposit-print-${c.date}`}
                              onClick={() => window.open(`/print-afstorting?id=${c.deposit_id}`, "_blank")}>
                              <Printer className="h-4 w-4" />
                            </Button>
                          )}
                        </span>
                      ) : (
                        <Button variant="outline" size="sm" data-testid={`closure-deposit-button-${c.date}`}
                          onClick={() => setDep({ date: c.date, amount: c.to_deposit ?? c.counted_cash ?? 0, by: "", at: new Date().toISOString().slice(0, 10), note: "" })}>
                          <Banknote className="h-4 w-4 mr-1" />{t("cash_deposit_register")}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{c.closed_by}{c.auto ? ` · ${t("cash_auto")}` : ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <Dialog open={!!dep} onOpenChange={(o) => !o && setDep(null)}>
        <DialogContent className="sm:max-w-md" data-testid="cash-deposit-dialog">
          <DialogHeader><DialogTitle className="font-display">{t("cash_deposit_register")} — {dep?.date}</DialogTitle></DialogHeader>
          {dep && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">{t("amount")}</Label>
                <Input type="number" step="0.01" value={dep.amount} onChange={(e) => setDep({ ...dep, amount: e.target.value })} data-testid="deposit-amount" /></div>
              <div className="space-y-1"><Label className="text-xs">{t("date")}</Label>
                <Input type="date" value={dep.at} onChange={(e) => setDep({ ...dep, at: e.target.value })} data-testid="deposit-date" /></div>
              <div className="space-y-1 col-span-2"><Label className="text-xs">{t("cash_deposit_by")}</Label>
                <Input value={dep.by} placeholder={t("cash_deposit_by_ph")} onChange={(e) => setDep({ ...dep, by: e.target.value })} data-testid="deposit-by" /></div>
              <div className="space-y-1 col-span-2"><Label className="text-xs">{t("cash_deposit_account")}</Label>
                <Input value={dep.bank_account || ""} onChange={(e) => setDep({ ...dep, bank_account: e.target.value })} data-testid="deposit-account" /></div>
              <div className="space-y-1 col-span-2"><Label className="text-xs">{t("note")}</Label>
                <Input value={dep.note} onChange={(e) => setDep({ ...dep, note: e.target.value })} data-testid="deposit-note" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDep(null)}>{t("cancel")}</Button>
            <Button onClick={saveDeposit} data-testid="deposit-save">{t("save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Panel>
  );
}
